/**
 * WebSocket transport: request/response envelopes multiplexed by id plus server-pushed events.
 * Reconnects with exponential backoff, except after the server closed the connection with 4004 (session replaced: the
 * account signed in somewhere else), when only the next request opens a new connection. On a re-established connection the reconnect handler (installed by
 * BackendClient: `auth.resume`) runs before queued requests are released, so no request ever reaches the
 * server on an unauthenticated connection.
 *
 * Browsers use the global `WebSocket`; Node 20 has none, so pass `WebSocketImpl` (e.g. `WebSocket` from the
 * `ws` package, which implements the same event-handler API).
 */
import type { EventEnvelope, RequestEnvelope, ResponseEnvelope } from '../../shared/protocol/envelope';
import { isEventEnvelope, isResponseEnvelope } from '../../shared/protocol/envelope';
import { WS_CLOSE_CODES } from '../../shared/protocol/model';
import { DEFAULT_REQUEST_TIMEOUT_MS, Emitter, type SendOptions, type Transport, TransportError, type TransportStatus } from './transport';

/** Subset of the WHATWG WebSocket API used by the transport. */
export interface WebSocketLike {
  readonly readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code?: number; reason?: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type WebSocketConstructor = new (url: string) => WebSocketLike;

export interface WebSocketTransportOptions {
  /** e.g. `wss://fwp.example.org/ws`. */
  url: string;
  WebSocketImpl?: WebSocketConstructor;
  timeoutMs?: number;
  reconnect?: boolean;
  maxBackoffMs?: number;
  /** How long a socket may stay CONNECTING before it is given up and replaced (default CONNECT_TIMEOUT_MS). */
  connectTimeoutMs?: number;
}

const OPEN = 1;

/**
 * A socket that has not opened after this long is closed and replaced (a connection attempt the network swallowed:
 * the browser's own timeout can take minutes, and until then every request waits for it).
 */
export const CONNECT_TIMEOUT_MS = 15_000;

/**
 * `wake()` (the app back from the background) replaces a socket that has been connecting for longer than this: it was
 * most likely started before the app slept and waits for an answer that will never come.
 */
export const WAKE_STALE_CONNECT_MS = 3_000;

/** The connection attempt in progress (connect()): its socket, since when, and how to give it up. */
interface OpenAttempt {
  ws: WebSocketLike;
  startedAt: number;
  abandon(reason: string, retryable?: boolean): void;
}

interface Pending {
  ws: WebSocketLike;
  resolve: (r: ResponseEnvelope) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

type ReconnectHandler = (send: (request: RequestEnvelope) => Promise<ResponseEnvelope>) => Promise<void>;

export class WebSocketTransport implements Transport {
  readonly kind = 'websocket' as const;
  readonly connectionBound = true;
  private ws: WebSocketLike | null = null;
  private opening: Promise<void> | null = null;
  private readonly pending = new Map<string, Pending>();
  private readonly events = new Emitter<EventEnvelope>();
  private readonly status = new Emitter<TransportStatus>();
  private readonly Impl: WebSocketConstructor;
  private closed = false;
  private everOpened = false;
  private backoff = 250;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectHandler: ReconnectHandler | null = null;
  private attempt: OpenAttempt | null = null;
  /** The server closed the connection with 4004 (session replaced): no automatic reconnect until the next request. */
  private halted = false;
  private readonly serverCloses = new Emitter<{ code: number; reason: string }>();

  constructor(private readonly opts: WebSocketTransportOptions) {
    const impl = opts.WebSocketImpl ?? (globalThis as { WebSocket?: WebSocketConstructor }).WebSocket;
    if (!impl) throw new Error('No WebSocket implementation available; pass WebSocketImpl');
    this.Impl = impl;
  }

  setReconnectHandler(handler: ReconnectHandler | null): void {
    this.reconnectHandler = handler;
  }

  connect(): Promise<void> {
    if (this.closed) return Promise.reject(new TransportError('Transport is closed', false));
    if (this.opening) return this.opening;
    if (this.ws && this.ws.readyState === OPEN) return Promise.resolve();
    this.halted = false;
    const reconnecting = this.everOpened;
    this.status.emit(reconnecting ? 'reconnecting' : 'connecting');
    let attempt: OpenAttempt | null = null;
    const opening = new Promise<void>((resolve, reject) => {
      let settled = false;
      let ws: WebSocketLike;
      try {
        ws = new this.Impl(this.opts.url);
      } catch (err) {
        reject(new TransportError(`Cannot open WebSocket: ${String(err)}`, true));
        return;
      }
      this.ws = ws;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const settle = () => {
        settled = true;
        if (timer) clearTimeout(timer);
        timer = null;
        if (this.attempt === attempt) this.attempt = null;
      };
      ws.onmessage = (ev) => this.onMessage(ev.data);
      ws.onerror = () => {
        /* followed by onclose */
      };
      ws.onopen = () => {
        if (timer) clearTimeout(timer); // open: the reconnect handler's requests have their own timeouts
        timer = null;
        this.backoff = 250;
        this.everOpened = true;
        const handler = reconnecting ? this.reconnectHandler : null;
        const release = () => {
          if (settled) return;
          settle();
          resolve();
          this.status.emit('open');
        };
        if (!handler) return release();
        handler((req) => this.sendOn(ws, req, this.opts.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS)).then(release, release);
      };
      ws.onclose = (ev) => {
        this.failPending(ws, new TransportError('WebSocket connection closed', true));
        const current = this.ws === ws;
        if (current) this.ws = null;
        if (!settled) {
          settle();
          reject(new TransportError('WebSocket connection failed', true));
        }
        const code = typeof ev?.code === 'number' ? ev.code : 0;
        // Replaced by a sign-in elsewhere: reconnecting would only resume a session that is gone (or, worse, lead a
        // client to sign in again by itself and end the other one). The app tells the player instead.
        if (current && code === WS_CLOSE_CODES.sessionReplaced) this.halted = true;
        if (current && code >= 4000 && code < 5000) this.serverCloses.emit({ code, reason: typeof ev?.reason === 'string' ? ev.reason : '' });
        if (current && !this.closed && !this.halted) this.scheduleReconnect();
      };
      // Given up (connect timeout, wake, restart): the socket is forgotten and closed, the attempt fails (retryable) and
      // the transport reconnects as after a lost connection.
      attempt = {
        ws,
        startedAt: Date.now(),
        abandon: (reason: string, retryable = true) => {
          if (settled) return;
          settle();
          ws.onopen = null;
          ws.onclose = null;
          ws.onmessage = null;
          this.failPending(ws, new TransportError(reason, retryable));
          const current = this.ws === ws;
          if (current) this.ws = null;
          if (this.opening === opening) this.opening = null;
          try {
            ws.close(4002, 'client gave up connecting');
          } catch {
            /* still connecting: the browser drops it */
          }
          reject(new TransportError(reason, retryable));
          if (current && !this.closed) this.scheduleReconnect();
        },
      };
      timer = setTimeout(() => attempt?.abandon('WebSocket connection timed out'), this.opts.connectTimeoutMs ?? CONNECT_TIMEOUT_MS);
    });
    this.opening = opening;
    this.attempt = attempt;
    const clear = () => {
      if (this.opening === opening) this.opening = null;
    };
    opening.then(clear, clear);
    return opening;
  }

  private scheduleReconnect(): void {
    if (this.closed || this.halted || this.opts.reconnect === false || this.reconnectTimer) return;
    this.status.emit('reconnecting');
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, this.opts.maxBackoffMs ?? 10_000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.ws && this.ws.readyState === OPEN) return;
      this.reconnectNow();
    }, delay);
  }

  /**
   * A new connection attempt; its failure schedules the next one. A failure that arrives after another attempt has
   * started (wake / restart gave this one up and opened the next) schedules nothing: that attempt does it when it fails.
   */
  private reconnectNow(): void {
    this.connect().catch(() => {
      if (!this.opening) this.scheduleReconnect();
    });
  }

  private onMessage(data: unknown): void {
    let msg: unknown;
    try {
      msg = JSON.parse(typeof data === 'string' ? data : String(data));
    } catch {
      return;
    }
    if (isResponseEnvelope(msg)) {
      const p = this.pending.get(msg.id);
      if (p) {
        this.pending.delete(msg.id);
        clearTimeout(p.timer);
        p.resolve(msg);
      }
    } else if (isEventEnvelope(msg)) {
      this.events.emit(msg);
    }
  }

  private failPending(ws: WebSocketLike | null, err: Error): void {
    for (const [id, p] of this.pending) {
      if (ws !== null && p.ws !== ws) continue;
      clearTimeout(p.timer);
      p.reject(err);
      this.pending.delete(id);
    }
  }

  private sendOn(ws: WebSocketLike, request: RequestEnvelope, timeoutMs: number): Promise<ResponseEnvelope> {
    if (ws.readyState !== OPEN) return Promise.reject(new TransportError('WebSocket is not open', true));
    if (this.pending.has(request.id)) return Promise.reject(new TransportError(`Duplicate request id ${request.id}`, false));
    return new Promise<ResponseEnvelope>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(request.id);
        reject(new TransportError('Request timed out', true));
      }, timeoutMs);
      this.pending.set(request.id, { ws, resolve, reject, timer });
      try {
        ws.send(JSON.stringify(request));
      } catch (err) {
        clearTimeout(timer);
        this.pending.delete(request.id);
        reject(new TransportError(`Send failed: ${String(err)}`, true));
      }
    });
  }

  async send(request: RequestEnvelope, opts: SendOptions = {}): Promise<ResponseEnvelope> {
    await this.connect();
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) throw new TransportError('WebSocket is not open', true);
    return this.sendOn(ws, request, opts.timeoutMs ?? this.opts.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS);
  }

  onEvent(listener: (event: EventEnvelope) => void): () => void {
    return this.events.on(listener);
  }

  onStatus(listener: (status: TransportStatus) => void): () => void {
    return this.status.on(listener);
  }

  onServerClose(listener: (close: { code: number; reason: string }) => void): () => void {
    return this.serverCloses.on(listener);
  }

  setAuthToken(): void {}

  wake(): void {
    if (this.closed || this.halted || this.opts.reconnect === false) return;
    if (this.ws && this.ws.readyState === OPEN) return;
    // A connection attempt from before the app slept: give it up instead of waiting for its timeout.
    const attempt = this.attempt;
    if (attempt && attempt.ws.readyState !== OPEN && Date.now() - attempt.startedAt >= WAKE_STALE_CONNECT_MS) attempt.abandon('WebSocket connection attempt stalled');
    this.backoff = 250;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (!this.opening) this.reconnectNow();
  }

  restart(): void {
    if (this.closed || this.halted) return;
    // A connection that does not answer (the client's probe failed): an attempt still in progress is given up too,
    // whatever its age, and a new one starts at once.
    if (this.attempt) {
      this.attempt.abandon('WebSocket connection restarted');
      return this.wake();
    }
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return this.wake();
    // A dead socket may never report its close: forget it now (its requests fail and are retried) and reconnect.
    this.ws = null;
    ws.onclose = null;
    ws.onmessage = null;
    this.failPending(ws, new TransportError('WebSocket connection restarted', true));
    try {
      ws.close(4001, 'client restart');
    } catch {
      /* already closing */
    }
    this.wake();
  }

  /** Test helper: drops the socket as if the network failed (triggers reconnect). */
  simulateDisconnect(): void {
    this.ws?.close(4000, 'simulated disconnect');
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.attempt?.abandon('Transport closed', false);
    this.failPending(null, new TransportError('Transport closed', false));
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, 1000);
        ws.onclose = () => {
          clearTimeout(t);
          resolve();
        };
        try {
          ws.close(1000, 'client closed');
        } catch {
          clearTimeout(t);
          resolve();
        }
      });
    }
    this.status.emit('closed');
  }
}
